import { formatCurrency } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    KalDate,
    KalDialogConfig,
    KalDialogService,
    KalSelectionModel,
    KalSnackbarConfig,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import difference from 'lodash-es/difference';
import orderBy from 'lodash-es/orderBy';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, skip, switchMap, tap } from 'rxjs/operators';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { SharedAdvancedSettlement } from 'src/app/_shared/classes/settlements/shared-advanced-settlement';
import { ConfirmDialogComponent } from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    SharedCreditsDepositsDialogComponent,
    SharedCreditsDepositsDialogData
} from 'src/app/_shared/dialogs/settlements/shared-credits-deposits-dialog/shared-credits-deposits-dialog.component';
import {
    SharedOverpaymentDialogComponent,
    SharedOverpaymentDialogData
} from 'src/app/_shared/dialogs/settlements/shared-overpaid-dialog/shared-overpayment-dialog.component';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { BeneficiaryPipe } from 'src/app/_shared/pipes/beneficiary.pipe';
import {
    SharedAdvancedSettlementGetInvoicesListAction,
    SharedAdvancedSettlementSaveAdvancedSettlementAction,
    SharedAdvancedSettlementSaveInvoiceWithDepositsAction
} from 'src/app/_shared/stores/settlements/shared-advanced-settlement/shared-advanced-settlement.actions';
import {
    SharedAdvancedSettlementState
} from 'src/app/_shared/stores/settlements/shared-advanced-settlement/shared-advanced-settlement.state';
import {
    SharedPaymentMethodsState
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.state';
import { UserState } from 'src/app/_user/store/user/user.state';
import { TypeReglement } from 'src/app/board/activity/activity.constants';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { arrayEmpty, arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { getISODate } from 'src/app/utils/date.utils';
import { graphqlErrors } from 'src/app/utils/errors.utils';
import { filterListFromSelection } from 'src/app/utils/list.utils';
import { environment } from 'src/environments/environment';
import {
    CriteresFactureDtoInput,
    FactureDto,
    FactureImputationAcompteAvoirDto,
    ListeFacturesEtListeReglementsModelDtoInput,
    MoyenPaiementDto,
    MutationVerifierMoyenPaiementValidArgs,
    ReglementModelDto,
    ReglementsReglementModelInput,
    StatusDtoEnum
} from 'src/graphql/generated-types';

export type SharedInvoiceType = FactureDto & { id: string, MontantControl: FormControl<number>, Libelle: string };

@Component({
    selector: 'app-shared-advanced-settlement',
    templateUrl: './shared-advanced-settlement.component.html',
    styleUrls: ['./shared-advanced-settlement.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAdvancedSettlementComponent<T extends FactureDto> extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

    invoicesList$: Observable<FactureDto[]> = inject(Store).select(SharedAdvancedSettlementState.invoicesList);

    @Output() invoicePaidByCreditDeposit = new EventEmitter<void>();

    paymentMethodDatasource: SharedAdvancedSettlement = null;

    dataSource: SharedInvoiceType[] = [];

    columns: KalTableHeader<SharedInvoiceType>[] = [
        {label: 'Libellé', code: 'Libelle'},
        {label: 'Montant facture', code: 'MontantFactureTotal'},
        {label: 'Acompte / Avoir', code: 'ListeFactureImpute'},
        {label: 'Montant à régler', code: 'Solde'},
        {label: 'Montants réglés', code: 'MontantControl'}
    ];

    invoicesList: T[] = [];

    shouldDisplayAllInvoices = false;

    shouldDisplayEditAmount = false;

    form: FormGroup;

    selection: KalSelectionModel<SharedInvoiceType> = null;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly snackbarService: KalSnackbarService,
                private readonly graphqlService: GraphqlService) {
        super(cdr);
    }

    get thirdId(): number {
        return this.snapshot(SharedAdvancedSettlementState.thirdId);
    }

    get selectedInvoicesList(): SharedInvoiceType[] {
        return this.selection ? filterListFromSelection(this.dataSource, this.selection, 'Id') : [];
    }

    get shouldDisplayOpenCreditDepositDialog(): boolean {
        return !this.selection && arrayOnlyOne(this.dataSource) && !this.isRefund;
    }

    get areAllSettlementsValid(): boolean {
        return this.settlementsArray.controls.every(e => e.value.isAdded);
    }

    get paymentType(): TypeReglement {
        return this.store.selectSnapshot(SharedAdvancedSettlementState.paymentType);
    }

    get settlementsArray(): FormArray {
        return this.form.get('settlements') as FormArray;
    }

    get displayedColumns(): string[] {
        return this.columns.filter(c => this.shouldDisplayEditAmount || (c.code !== 'MontantControl')).map(c => c.code as string);
    }

    // <editor-fold defaultstate="collapsed" desc="Displayed amounts">

    get countTotalInvoicesAmount(): number {
        return this.dataSource.reduce((accumulator, {MontantFactureTotal}) => accumulator + MontantFactureTotal, 0);
    }

    get countCreditsDepositsAmount(): number {
        let amount = 0;
        this.dataSource.forEach((invoice) => {
            amount += this.getCreditDepositAmount(invoice);
        });

        return amount;
    }

    get countTotalAmountToPay(): number {
        return this.dataSource.reduce((accumulator, {Solde}) => accumulator + Solde, 0);
    }

    get countTotalEditedAmount(): number {
        const invoicesIdList = this.dataSource.map(e => e.Id);
        const filters = (invoice: SharedInvoiceType): boolean => invoicesIdList.indexOf(invoice.Id) > -1;
        return this.getInvoicesAmount(filters);
    }

    get amountToPay(): number {
        let invoicesAmount = 0;

        if (!this.shouldDisplayAllInvoices) {
            invoicesAmount = this.shouldDisplayEditAmount ? this.countTotalEditedAmount : this.countTotalAmountToPay;
        } else {
            // si on est en mode édition des montants
            if (this.shouldDisplayEditAmount) {
                const filters = (invoice: SharedInvoiceType): boolean => this.selectedInvoicesList.some(({Id}) => Id === invoice.Id);
                invoicesAmount = this.getInvoicesAmount(filters);
            } else {
                invoicesAmount = this.selectedInvoicesList.reduce((acc, {Solde}) => acc + Solde, 0);
            }
        }

        return invoicesAmount - this.addedSettlementAmount;
    }

    get editAmountLabel(): string {
        if (this.shouldDisplayEditAmount) {
            return 'Ne plus modifier les montants';
        }

        return this.isRefund
            ? 'Modifier les montants à rembourser'
            : 'Modifier les montants à régler';
    }

    get addedSettlementAmount(): number {
        return this.settlementsArray.controls
            .filter(({value}) => !!value.isAdded)
            .reduce((acc, {value}) => acc + (value?.settlement?.MontantReglement || 0), 0);
    }

    get maxAmount(): number {
        return Math.max(this.amountToPay, 0);
    }

    get invoicesLinkLabel(): string {
        const countInvoices = this.invoicesList.filter(i => !i.IsAvoir || !i.IsAccompte).length;
        const countCreditsAndDeposits = this.invoicesList.length - countInvoices;
        const supplierOrBeneficiaryLabel = this.currentInvoice?.Fournisseur ? 'fournisseur' : 'bénéficiaire';

        let label = countInvoices + (countInvoices > 1 ? ' factures' : ' facture') + ' à régler';

        if (countCreditsAndDeposits > 0) {
            label += (countCreditsAndDeposits + (countCreditsAndDeposits === 1 ? ' avoir / acompte' : ' avoirs / acomptes'));
        }

        return label + ' pour ce ' + supplierOrBeneficiaryLabel;
    }

    get paymentMethodsList(): MoyenPaiementDto[] {
        return this.store.selectSnapshot(SharedPaymentMethodsState.paymentMethodsList);
    }

    get canPay(): boolean {
        return this.maxAmount === 0 && this.settlementsArray.controls.every(({value}) => value.isAdded);
    }

    // </editor-fold>

    get currentInvoice(): T {
        return this.invoicesList.find(e => e.Id === this.store.selectSnapshot(SharedAdvancedSettlementState.selectedInvoiceId));
    }

    get hasMoreThanOneInvoice(): boolean {
        return this.invoicesList.length > 1;
    }

    get hasInvoicesSelected(): boolean {
        return !this.selection.isEmpty();
    }

    get isRefund(): boolean {
        return [
            TypeReglement.RemboursementAvoirFournisseur,
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.AcompteFournisseur
        ].some(type => type === this.paymentType);
    }

    get title(): string {
        if (arrayEmpty(this.dataSource)) {
            return '';
        }

        const plural = (): string => this.dataSource.length > 1 ? 's' : '';
        const {Prenom, Nom, CodeMembre} = this.dataSource[0]?.Benef || {};
        const beneficiary = BeneficiaryPipe.format(Prenom, Nom, CodeMembre);

        const isAsset = this.dataSource.every(i => i.IsAvoir);
        if (isAsset) {
            const title = `Avoir${plural()}`;
            if (this.isRefund && this.currentInvoice?.Fournisseur) {
                return `${title} concernant ${this.currentInvoice.Fournisseur.Nom}`;
            }

            return `Avoir${plural()} à rembourser`;
        }

        const isDeposit = this.dataSource.every(i => i.IsAccompte);
        if (isDeposit) {
            return `Acompte${plural()} à ${this.isRefund ? 'rembourser' : 'régler'}`;
        }

        return `Facture${plural()} à régler par ${beneficiary}`;
    }

    get currentInvoiceMaxAmount(): number {
        if (arrayEmpty(this.dataSource)) {
            return 0;
        }
        return this.dataSource[0].Solde;
    }

    get shouldInvoiceListValid(): boolean {
        return this.dataSource.every(e => e.MontantControl.valid);
    }

    getCreditDepositAmount(invoice: SharedInvoiceType): number {
        return (invoice?.ListeFactureImpute?.FactureImputationAcompteAvoirDTO || []).reduce((accumulator, {MontantImputation}) => {
            return accumulator + MontantImputation;
        }, 0);
    }

    // <editor-fold defaultstate="collapsed" desc="Handle payment method">

    addSettlement(): void {
        this.settlementsArray.push(this.createSettlementForm());
    }

    deleteSettlement(index: number): void {
        this.settlementsArray.removeAt(index);

        if (this.settlementsArray.controls.length === 0) {
            this.settlementsArray.push(this.createSettlementForm());
        }
    }

    applySettlement(index: number): void {
        const settlementControl = this.settlementsArray.controls[index];
        const settlementValue = settlementControl.value?.settlement || null;
        const articlePayment = settlementValue?.MoyenPaiement?.Article || null;

        const results = articlePayment
            ? this.checkArticlePayment(settlementValue)
            : of(null);

        this.watch$ = results.pipe(
            filter(() => settlementControl.valid),
            tap(() => settlementControl.patchValue({isAdded: true})),
            catchError(err => {
                const message = graphqlErrors(err);
                const code = +graphqlErrors(err, 'code');
                if (code === 1) {
                    const isDepositRefund = !!this.invoicesList?.[0]?.IsAccompte;
                    const isEntryContext = this.paymentType === TypeReglement.Beneficiaire
                        || this.paymentType === TypeReglement.RemboursementAvoirFournisseur
                        || (isDepositRefund && !!this.thirdId);

                    // Dans un contexte d'entrée, on ajoutera l'article en stock, de ce fait ce n'est pas la peine de vérifier la quantité dispo
                    // La businessException correspondant au code 1 est celle validant la quantité en stock
                    if (!isEntryContext) {
                        this.dialogService.open(ConfirmDialogComponent, {
                            data: {
                                body: message,
                                cancelLabel: 'Fermer'
                            },
                            hasBackdrop: true,
                            backdropClass: 'cdk-overlay-dark-backdrop',
                        });
                        settlementControl.patchValue({isAdded: true});
                        return null;
                    }
                }
                this.snackbarService.open(new KalSnackbarConfig({title: message}));
                return throwError(err);
            }),
        );
    }

    getPaymentMethodLabel(settlement: ReglementModelDto): string {
        return settlement.MoyenPaiement?.Libelle || null;
    }

    isChequeNumberDisplayed(settlement: ReglementModelDto): boolean {
        return this.getPaymentMethodCode(settlement) === 'CHQ' && !!settlement?.NumCheque;
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Invoices list">

    openInvoicesList(): void {
        this.shouldDisplayAllInvoices = true;
        this.resetSettlementInWorking();
        this.settlementsArray.disable();

        this.selection = new KalSelectionModel<SharedInvoiceType>({
            multiple: true,
            added: this.dataSource,
            numberOfItems: this.invoicesList.length
        });

        this.dataSource = this.getInvoiceList(this.invoicesList || []);

        this.cdr.markForCheck();
    }

    closeInvoicesList(): void {
        this.shouldDisplayAllInvoices = false;

        const displayedInvoicesIdList = this.dataSource.map(e => e.Id);
        const selectedInvoicesIdList = this.selectedInvoicesList.map(e => e.Id);
        this.settlementsArray.enable();

        if (difference(selectedInvoicesIdList, displayedInvoicesIdList).length > 0
            || difference(displayedInvoicesIdList, selectedInvoicesIdList).length > 0 || this.selection.all) {
            this.clearSettlements();
        }

        this.dataSource = this.selectedInvoicesList;
        this.selection = null;

        this.cdr.markForCheck();
    }

    toggleShouldEditAmount(): void {
        this.shouldDisplayEditAmount = !this.shouldDisplayEditAmount;
        this.cdr.markForCheck();
    }

    openCreditsDepositsDialog(): void {
        if (!this.shouldDisplayOpenCreditDepositDialog) {
            return;
        }

        const config = new KalDialogConfig<SharedCreditsDepositsDialogData<T>>({
            title: 'Imputation des avoirs et acomptes',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                invoice: this.dataSource[0]
            }
        });
        const dialogRef = this.dialogService.open(SharedCreditsDepositsDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => result?.accepted),
            switchMap((result: SharedCreditsDepositsDialogData<T>) =>
                this.attachDepositToInvoice(
                    result.appliedDeposits,
                    cloneDeep(this.dataSource[0]),
                    result.allocationDate
                )
            )
        );
    }

    save(): Observable<any> {
        if (!this.thirdId) {
            return throwError(() => 'Le numéro du tiers n\'est pas renseigné');
        }

        const value = this.snapshot(SharedAdvancedSettlementState.paymentType);
        let entryType;

        switch (value) {
            case TypeReglement.AcompteClient:
                entryType = TypeReglement.RemboursementAvoirClient;
                break;
            case TypeReglement.AcompteFournisseur:
                entryType = TypeReglement.RemboursementAvoirFournisseur;
                break;
            default:
                entryType = value;
                break;
        }

        const advancePaymentObject: ListeFacturesEtListeReglementsModelDtoInput = {
            Benef: this.paymentMethodDatasource.isClientContext || this.paymentMethodDatasource.isClientDeposit ? { Id: this.thirdId } : null,
            Fournisseur: this.paymentMethodDatasource.isProviderContext ? {
                Id: this.thirdId,
                Nom: this.snapshot(SharedPaymentMethodsState.third)?.Nom || null,
            } : null,
            Caisse: {Id: this.snapshot(UserState.userContext).CurrentCaisse},
            ListeFacture: {
                ReglementFactureModelDTO: this.dataSource.map(invoice => {
                    return {
                        Activite: {Id: this.snapshot(ActivitySalesState.currentActivityId)},
                        Facture: invoice,
                        PaiementClient: Math.min(invoice.Solde, +(invoice.MontantControl.value || 0)),
                        IsEnableMonPaiement: true,
                        IsSelected: true
                    };
                })
            },
            ListeReglementModel: {
                ReglementModelDTO: this.settlementsArray.controls.map((p: FormGroup) => p.value.settlement).map(v =>
                    // #58426 - On force l'arrondi à 2 chiffres après la virgule maximum pour éviter les 0.999999999 et avoir 1 par ex
                    ({...v, MontantReglement: Number.parseFloat(v.MontantReglement.toFixed(2))})
                )
            },
            TypeEcriture: entryType,
            User: {Id: this.snapshot(UserState.userContext).CurrentUserId}
        };

        let action = of(advancePaymentObject);

        if (this.amountToPay < 0 && this.paymentType === TypeReglement.Beneficiaire) {
            action = this.openOverpayementPopUp(Math.abs(this.amountToPay)).pipe(
                map(({payment, overpaymentAccountId, overpaymentAmount}) => {
                    // On ajoute la liste des règlements à l'objet de paiement avancé
                    advancePaymentObject.ListeReglements = {ReglementsReglementModel: [payment]};

                    // On récupère le dernier moyen de paiement pour y ajouter les informations du trop perçu
                    const lastPayment = advancePaymentObject.ListeReglementModel.ReglementModelDTO[advancePaymentObject.ListeReglementModel.ReglementModelDTO.length - 1];
                    lastPayment.IdCompteTropPercu = overpaymentAccountId;
                    lastPayment.MontantTropPercu = overpaymentAmount;

                    return advancePaymentObject;
                })
            );
        }

        return action.pipe(
            tap(() => this.loading = true),
            switchMap(e => this.dispatch$(new SharedAdvancedSettlementSaveAdvancedSettlementAction(e))),
            tap(() => this.loading = false),
            catchError((error) => {
                this.loading = false;
                return throwError(error);
            })
        );
    }

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    normalize(value: SharedInvoiceType): T {
        const {MontantControl, id, Libelle, ...invoice} = value;
        invoice.Solde = +MontantControl.value;
        return invoice as T;
    }

    // </editor-fold>

    private getInvoicesAmount(filters: (invoice: SharedInvoiceType) => boolean): number {
        return this.dataSource
            .filter(e => filters(e))
            .map(({Solde, MontantControl}) => Math.min(Solde, +(MontantControl.value || 0)))
            .reduce((acc, amount) => acc + amount, 0);
    }

    private checkArticlePayment(payment: any): Observable<any> {
        const paymentAmount = payment.MontantReglement || 0;
        const articlePayment = payment?.MoyenPaiement?.Article || null;

        return this.graphqlService.mutateOne<'VerifierMoyenPaiementValid', MutationVerifierMoyenPaiementValidArgs>({
            mutation: gql`
                mutation VerifierMoyenPaiementValid($contexte: ContextInfosInput, $article: ArticleDTOInput, $montantReg: Float) {
                    VerifierMoyenPaiementValid(contexte: $contexte, article: $article, montantReg: $montantReg)
                }
            `,
            variables: {
                article: articlePayment,
                montantReg: paymentAmount
            },
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        });
    }

    private getPaymentMethodCode(settlement: ReglementModelDto): string {
        return settlement.MoyenPaiement?.TypeMoyenPaiement?.Code || null;
    }

    // <editor-fold defaultstate="collapsed" desc="Private">

    private openOverpayementPopUp(overpaymentAmount: number): Observable<{
        payment: ReglementsReglementModelInput,
        overpaymentAccountId: number,
        overpaymentAmount: number
    }> {
        this.loading = false;

        // On récupère la date de la facture la plus récente pour un control dans le cas d'un remboursement
        const latestInvoiceDate = this.dataSource.reduce((latestDate: KalDate, invoice) => {
            const invoiceDate = new KalDate(invoice.DateFacture, environment.date.format.isoQuery);
            if (latestDate === null) {
                return invoiceDate;
            }
            return invoiceDate.isAfter(latestDate) ? invoiceDate : latestDate;
        }, null);

        const data: SharedOverpaymentDialogData = {
            overpaymentAmount,
            latestInvoiceDate,
            beneficiary: this.dataSource[0]?.Benef || null,
            invoices: this.dataSource.map(e => this.normalize(e))
        };

        const config = new KalDialogConfig<SharedOverpaymentDialogData>({
            hasBackdrop: true,
            title: `Gestion du trop perçu de ${formatCurrency(overpaymentAmount, 'fr-FR', '€')}`,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data
        });

        const dialogRef = this.dialogService.open(SharedOverpaymentDialogComponent, config);

        return dialogRef.afterClosed.pipe(
            filter(result => result?.accepted),
            map(({payment, overpaymentAccountId}: SharedOverpaymentDialogData) => {
                return {payment, overpaymentAccountId, overpaymentAmount};
            })
        );
    }

    private clearSettlements(): void {
        this.settlementsArray.clear();
        this.settlementsArray.push(this.createSettlementForm());

        const invoicesIdList = this.selectedInvoicesList.map(e => e.Id);
        this.watch$ = this.paymentMethodDatasource.changeInvoicesList$(invoicesIdList);
    }

    private createSettlementForm(): FormGroup {
        return this.fb.group({
            isAdded: false,
            settlement: null
        });
    }

    private attachDepositToInvoice(appliedDeposits: { deposit: T, amountToApply: number }[], invoice: SharedInvoiceType, allocationDate: KalDate): Observable<any> {
        const selected: T = {
            ...this.normalize(invoice),
            ListeAcompteAvoirImpute: {
                FactureImputationAcompteAvoirDTO: [
                    ...(invoice?.ListeAcompteAvoirImpute?.FactureImputationAcompteAvoirDTO || []),
                    ...appliedDeposits.map<FactureImputationAcompteAvoirDto>(({amountToApply, deposit}) => {
                        return {
                            Status: StatusDtoEnum.Inserted,
                            Facture: {Id: invoice.Id},
                            Avoir: deposit,
                            MontantImputation: amountToApply,
                            DateImputation: getISODate(allocationDate)
                        };
                    })
                ]
            },
        };

        this.loading = true;

        return this.dispatch$(new SharedAdvancedSettlementSaveInvoiceWithDepositsAction(selected)).pipe(
            tap(() => {
                const creditDepositAmount = this.getCreditDepositAmount(invoice);
                const appliedCreditDepositAmount = appliedDeposits.reduce((accumulator, {amountToApply}) => accumulator + amountToApply, 0);

                // Lorsque les imputations correspondent au total facturé, la facture est entièrement soldée
                if (invoice.MontantFactureTotal === (creditDepositAmount + appliedCreditDepositAmount)) {
                    const config = new KalSnackbarConfig({
                        title: 'La facture a été soldée par imputation d\'avoir / acompte.'
                    });
                    this.snackbarService.open(config);

                    this.invoicePaidByCreditDeposit.emit();
                } else {
                    this.dispatchInvoicesList();
                }

                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    // </editor-fold>

    private dispatchInvoicesList(): void {
        const criterias: CriteresFactureDtoInput = {
            BenefId: this.paymentMethodDatasource.isClientContext || this.paymentMethodDatasource.isClientDeposit ? this.thirdId : null,
            IdFournisseur: this.paymentMethodDatasource.isProviderContext ? this.thirdId : null,
            IsDernierFactClient: false,
            IsFactureClient: false,
            IsFactureFournisseur: false,
            IsFactureSoldeDifferentDeZero: true,
            TakeActivite: true,
            TakeBenef: true,
            TakeFournisseur: true,
            TakeCommande: true,
            ...(this.snapshot(UserState.hasMultiDelegationsModule) ? {
                TakeDelegationCible: true,
                TakeDelegationSource: true,
            } : {}),
            TakeDelegationCreerPar: true,
            TakeFacturesImputeesComplet: true,
            TakeAvoirEtLigneAvoir: true,
            WithFacture: true,
            WithAvoir: false
        };

        const idList = this.snapshot(SharedAdvancedSettlementState.invoicesListIdForCriterias);
        if (arrayNotEmpty(idList)) {
            criterias.ListeIdFacture = idList.join(',');
        }

        if ([TypeReglement.ReglementPretBenef, TypeReglement.DonCEBenef].includes(this.paymentType)
            || (this.isRefund && this.paymentType !== TypeReglement.AcompteFournisseur)) {
            criterias.WithAvoir = true;
            criterias.WithFacture = false;
        }

        if ([TypeReglement.AcompteClient,
            TypeReglement.AcompteFournisseur
        ].indexOf(this.paymentType) !== -1) {
            criterias.WithAccompte = true;
            criterias.TakeCommandeAcompte = true;
            criterias.WithFacture = false;
        }

        criterias.IsFactureHide = !!this.snapshot(SharedAdvancedSettlementState.shouldDisplayHiddenInvoice);

        this.loading = true;

        this.watch$ = this.dispatch$(new SharedAdvancedSettlementGetInvoicesListAction(criterias)).pipe(
            tap(() => this.loading = false),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }

    private getInvoiceList(invoiceList: FactureDto[]): SharedInvoiceType[] {
        return invoiceList.map(e => {
            const invoiceTypes = {
                IsFacture: 'Facture',
                IsAvoir: 'Avoir',
                IsAccompte: 'Acompte',
            };
            const invoiceType = invoiceTypes[Object.keys(invoiceTypes).find(k => !e.IsAccompte && !e.IsAvoir ? k === 'IsFacture' : e[k])];

            let label = `${invoiceType} ${e.NumFacture || ''}`;

            if (e.Objet) {
                label += (' - ' + e.Objet);
            }

            return {
                ...e,
                id: '' + e.Id,
                Libelle: label,
                MontantControl: new FormControl(e.Solde, [Validators.min(0), Validators.max(e.Solde)]),
            };
        });
    }

    private resetSettlementInWorking(): void {
        const indexSettlement = this.settlementsArray.controls.findIndex(e => !e.value.isAdded);

        if (indexSettlement > -1) {
            this.deleteSettlement(indexSettlement);
        }
    }

    /**
     * Réinitialisation de la sélection lorsque le datasource a été modifié
     */
    private reinitializeSelection(): void {
        if (!this.selection) {
            return;
        }

        const selection = this.dataSource.filter(e =>
            filterListFromSelection(this.dataSource, this.selection, 'Id')
                .map(({Id}) => Id)
                .indexOf(e.Id) > -1);

        this.selection.clear(false);
        this.selection.select(...selection);
    }

    ngOnInit(): void {
        this.paymentMethodDatasource = new SharedAdvancedSettlement(this.store, this.snapshot(SharedAdvancedSettlementState.activityId), this.paymentType, this.thirdId);

        this.form = this.fb.group({
            settlements: this.fb.array([this.createSettlementForm()])
        });

        this.dispatchInvoicesList();

        this.watch$ = this.invoicesList$.pipe(
            skip(1),
            filter(invoicesList => arrayNotEmpty(invoicesList)),
            tap((invoicesList: FactureDto[]) => {
                let filteredInvoiceList = invoicesList.filter(invoice => {
                    return !invoice.IsSupprimer
                        && ([invoice.CEDelegationCreePar, invoice.CEDelegationSource, invoice.CEDelegationCible].some(e => !e)
                            || invoice.CEDelegationCreePar.Id !== invoice.CEDelegationCible.Id
                            || invoice.CEDelegationCreePar.Id !== invoice.CEDelegationSource.Id
                            || invoice.CEDelegationCreePar.Id === this.snapshot(UserState.currentDelegation).Id
                        );
                });

                if (this.paymentType === TypeReglement.AcompteFournisseur) {
                    filteredInvoiceList = filteredInvoiceList.filter(invoice => invoice.IsAccompte);
                }

                this.invoicesList = orderBy(filteredInvoiceList || [], 'Id', 'desc') as T[];

                this.dataSource = this.shouldDisplayAllInvoices
                    ? this.getInvoiceList(this.invoicesList)
                    : this.getInvoiceList(this.currentInvoice ? [this.currentInvoice] : []);

                this.paymentMethodDatasource.invoiceNumber = this.currentInvoice?.NumFacture;

                this.watch$ = this.paymentMethodDatasource.changeInvoicesList$(this.dataSource.map(({Id}) => Id));
                this.reinitializeSelection();

                this.cdr.markForCheck();
            })
        );
    }

    ngAfterViewInit(): void {
        this.watch$ = this.paymentMethodDatasource.initPaymentMethods();
    }

}
